import React, {createRef, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import Administration from "../../session/Administration";
import {
	Application,
	ConfigurationType,
	NewsPanel,
	NewsPanelEntry
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import NewsDialogFields from "../field/NewsDialogFields";
import {InputSwitch} from "primereact/inputswitch";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import TimeUnit from "../../../shared/units/TimeUnit";
import {Menu} from "primereact/menu";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import Info, {InfoType} from "../../../shared/components/Info";

export class NewsDialogDefinition extends AdminDialog<NewsPanel,
	AdminDialogProperties<NewsPanel>, AdminDialogState<NewsPanel>> {
	public renderContents(): ReactNode {
		let parameter: NewsPanel | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let entry: Array<NewsPanelEntry> = FunctionHelper.applyValue(parameter?.entry, NewsPanel.getEntryDefault());
		let hasNews: boolean = entry.length > 0;
		let menu: RefObject<Menu> = createRef<Menu>();
		let newsUrl: string = hasNews && FunctionHelper.exists(entry[0]) ?
			FunctionHelper.applyValue(entry[0].url, NewsPanelEntry.getUrlDefault()) :
			NewsPanelEntry.getUrlDefault();

		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-news-enabled">
						<Translate {...this.props} data="news.enabled.label" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-8 switch-container">
						<InputSwitch inputId="control-news-enabled" checked={hasNews}
									 onChange={(e) => this.onChange(
										 NewsDialogFields.ENABLED, e.value
									 )}/>
						<label className="switch-label" htmlFor="control-news-enabled">
							<Translate {...this.props} data="news.enabled.value" namespace="dialog"/>
						</label>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!hasNews}>
					<label className="p-col-12 p-sm-4" htmlFor="control-news-url">
						<Translate {...this.props} data="news.url.label" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={NewsPanelEntry.getUrlDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<div className="p-inputgroup">
							<InputText id="control-news-url" value={newsUrl}
									   onInput={(e) => this.onChange(
										   NewsDialogFields.URL, e.currentTarget.value
									   )}/>
							<Button icon="icon icon-angle-down" aria-controls={"control-news-url-menu"}
									onClick={(event) => menu.current!.toggle(event)}
									className="p-inputgroup-addon" aria-haspopup={true}/>
							<Menu id="control-news-url-menu" popup={true} ref={menu}
								  appendTo={document.getElementById("root")}
								  model={[
									  {
										  label: Translate.value(
											  "news.url.language_portal", {ns: "dialog"}
										  ),
										  command: (e) => this.onChange(
											  NewsDialogFields.URL, newsUrl + "{languagePortal}"
										  )
									  },
									  {
										  label: Translate.value(
											  "news.url.language_browser", {ns: "dialog"}
										  ),
										  command: (e) => this.onChange(
											  NewsDialogFields.URL, newsUrl + "{languageBrowser}"
										  )
									  },
									  {
										  label: Translate.value("news.url.country", {ns: "dialog"}),
										  command: (e) => this.onChange(
											  NewsDialogFields.URL, newsUrl + "{country}"
										  )
									  }
								  ]}/>
						</div>
					</div>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.news.url"} namespace={"help"} markdown={true}/>
					</Helper>
				</div>
				<div className="p-col-12 p-grid" hidden={!hasNews}>
					<label className="p-col-12 p-sm-4" htmlFor="control-news-interval">
						<Translate {...this.props} data="news.interval" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={NewsPanel.getContentRefreshIntervalDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputNumber inputId="control-news-interval" step={1} showButtons={true} useGrouping={false}
									 suffix={" " + TimeUnit.SECOND}
									 value={FunctionHelper.applyValue(
										 parameter?.contentRefreshInterval, NewsPanel.getContentRefreshIntervalDefault()
									 )}
									 min={NewsPanel.getContentRefreshIntervalMin()}
									 onChange={(e) => this.onChange(
										 NewsDialogFields.INTERVAL, e.value
									 )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["news-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.news.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.admin.news.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-news"}/>
					<Translate {...this.props} data="news.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: NewsDialogFields, value: any): void {
		let parameter: NewsPanel | undefined = this.state.parameter as NewsPanel;

		switch (field) {
			case NewsDialogFields.ENABLED:
				parameter.entry = value ? [NewsPanelEntry.fromJson({
					url: ""
				})] : NewsPanel.getEntryDefault();
				break;
			case NewsDialogFields.URL:
				parameter.entry![0].url = value;
				break;
			case NewsDialogFields.INTERVAL:
				parameter.contentRefreshInterval = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let applicationConfiguration: Application = (
			await administration.getConfiguration(
				ConfigurationType.Application
			) as Application
		).clone();
		applicationConfiguration.portal.newsPanel = this.state.parameter;

		try {
			await administration.updateConfiguration(ConfigurationType.Application, applicationConfiguration);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.news", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.NEWS;
	}
}

const NewsDialog = React.memo(NewsDialogDefinition,
	(
		prevProps: Readonly<AdminDialogProperties<NewsPanel>>,
		nextProps: Readonly<AdminDialogProperties<NewsPanel>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default NewsDialog;